/**
 * Make Offer — frontend controller
 *
 * Responsibilities (frontend only — see report for backend requirements):
 *  - Open/close the offer modal (click, overlay click, cancel, ESC, focus trap)
 *  - Lock page scroll while open, restore it on close
 *  - Track the currently selected variant so the offer is always submitted
 *    against the correct variant id/price. FurbX's <variant-radios>/
 *    <variant-selects> (assets/global.js) update the hidden
 *    `input[name="id"]` inside the product form and fire a `change` event
 *    on it whenever the shopper picks a different option — we listen for
 *    that and re-resolve the full variant object from the JSON the snippet
 *    embeds (`[data-make-offer-variants]`).
 *  - Client-side validation (empty / non-numeric / zero / negative / below minimum)
 *  - POST the offer to a Shopify App Proxy endpoint (see APP_PROXY_PATH below)
 *
 * All customer-facing copy comes from locales/en.default.json via the
 * [data-make-offer-strings] JSON block, so nothing is hard-coded in English.
 *
 * IMPORTANT: this file never decides whether an offer is accepted. Client-side
 * validation only prevents obviously-bad submissions before hitting the network;
 * price validation, offer-ID generation and accept/reject/counter logic all
 * happen server-side (see README/report — Draft Order + backend service).
 */
(function () {
  'use strict';

  // Build stamp — check this in the browser console to confirm which version
  // of this file the storefront is actually serving. Shopify's CDN caches
  // assets aggressively, so an old copy can survive a theme upload.
  var BUILD = 'make-offer-v5';
  window.MakeOfferBuild = BUILD;
  console.log('[make-offer] build:', BUILD);

  // Where offers are sent. Configured per-product on the modal element by
  // snippets/make-offer.liquid via data-make-offer-mode:
  //
  //   "contact"   (default) — posts through Shopify's own contact form
  //                endpoint (/contact). Shopify emails the submission to the
  //                address in Settings > Notifications. No app, no server,
  //                works the moment the theme is published.
  //
  //   "app_proxy" — posts JSON to /apps/make-offer/submit. Requires the
  //                backend app + App Proxy to be configured in the Partner
  //                Dashboard (see "App Proxy setup" in the implementation
  //                report). Switch data-make-offer-mode once that is live so
  //                offers become real Draft Orders with accept/reject logic.
  var DEFAULT_PROXY_PATH = '/apps/make-offer/submit';
  var DEFAULT_CONTACT_PATH = '/contact';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var DEFAULT_STRINGS = {
    offer_required: 'Please enter an offer amount.',
    offer_positive: 'Your offer must be greater than zero.',
    offer_minimum: 'Please enter an offer of at least __amount__.',
    name_required: 'Please enter your name.',
    email_invalid: 'Please enter a valid email address.',
    submit_failed: "We couldn't submit your offer right now. Please contact us directly or try again later.",
    success: "Thanks! Your offer has been sent. We'll email you our decision shortly."
  };

  // Number of modals currently open — the body lock is only released
  // when the last one closes.
  var openCount = 0;
  var savedScrollY = 0;

  var FLAG_KEY = 'make-offer:submitted';

  function writeFlag(id) {
    try {
      window.sessionStorage.setItem(FLAG_KEY, id);
    } catch (e) {
      /* private mode — the modal simply will not auto-reopen */
    }
  }

  function readFlag() {
    try {
      return window.sessionStorage.getItem(FLAG_KEY);
    } catch (e) {
      return null;
    }
  }

  function clearFlag() {
    try {
      window.sessionStorage.removeItem(FLAG_KEY);
    } catch (e) {
      /* nothing to clean up */
    }
  }

  function lockScroll() {
    if (openCount === 0) {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.classList.add('make-offer-modal-open', 'overflow-hidden');
    }
    openCount += 1;
  }

  function unlockScroll() {
    openCount = Math.max(0, openCount - 1);
    if (openCount === 0) {
      document.body.classList.remove('make-offer-modal-open', 'overflow-hidden');
      window.scrollTo(0, savedScrollY);
    }
  }

  function formatMoney(cents, format) {
    if (typeof cents !== 'number') return '';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var formatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    if (format && format.indexOf('{{amount}}') !== -1) {
      return format.replace('{{amount}}', formatted);
    }
    return formatted;
  }

  function parseJSONFrom(root, selector, fallback) {
    var el = root.querySelector(selector);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  // Liquid's `t` filter HTML-escapes its output, so apostrophes arrive as
  // &#39; and would render literally when written via textContent.
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    var el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  function decodeStrings(map) {
    var out = {};
    Object.keys(map).forEach(function (key) {
      out[key] = decodeEntities(map[key]);
    });
    return out;
  }

  function MakeOfferModal(root) {
    this.root = root;
    this.dialog = root.querySelector('.make-offer-modal__dialog');
    this.trigger =
      document.querySelector('[data-make-offer-trigger][aria-controls="' + root.id + '"]') ||
      document.querySelector('[data-make-offer-trigger]');
    this.form =
      root.querySelector('[data-make-offer-form]') ||
      document.getElementById(root.getAttribute('data-make-offer-form-id')) ||
      root.querySelector('form');
    this.bodyField = root.querySelector('[data-make-offer-body]');
    this.variantIdField = root.querySelector('[data-make-offer-field="variant_id"]');
    this.priceEl = root.querySelector('[data-make-offer-current-price]');
    this.variantTitleEl = root.querySelector('[data-make-offer-variant-title]');
    this.imageEl = root.querySelector('[data-make-offer-image]');
    this.submitBtn = root.querySelector('[data-make-offer-submit]');
    this.spinner = root.querySelector('[data-make-offer-spinner]');
    this.formError = root.querySelector('[data-make-offer-form-error]');
    this.success = root.querySelector('[data-make-offer-success]');
    this.minimumOffer = parseFloat(root.getAttribute('data-minimum-offer') || '0');
    this.currentVariantPrice = null; // cents, kept in sync with the visible price
    this.variants = parseJSONFrom(root, '[data-make-offer-variants]', []);
    this.strings = decodeStrings(
      Object.assign({}, DEFAULT_STRINGS, parseJSONFrom(root, '[data-make-offer-strings]', {}))
    );
    this.mode = root.getAttribute('data-make-offer-mode') || 'contact';
    this.contactUrl = root.getAttribute('data-make-offer-contact-url') || DEFAULT_CONTACT_PATH;
    this.proxyUrl = root.getAttribute('data-make-offer-proxy-url') || DEFAULT_PROXY_PATH;
    this.productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    this.isOpen = false;

    this.onKeydown = this.onKeydown.bind(this);
    this.bind();
    this.reopenAfterPost();
  }

  MakeOfferModal.prototype.t = function (key, replacements) {
    var value = this.strings[key] || DEFAULT_STRINGS[key] || '';
    if (replacements) {
      Object.keys(replacements).forEach(function (name) {
        value = value.split('__' + name + '__').join(replacements[name]);
      });
    }
    return value;
  };

  MakeOfferModal.prototype.bind = function () {
    var self = this;

    if (this.trigger) {
      this.trigger.addEventListener('click', function () {
        self.open();
      });
    }

    this.root.querySelectorAll('[data-make-offer-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.close();
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        self.onSubmit(event);
      });
    }

    // Stay in sync with whichever variant is currently selected on the page,
    // so an offer is never submitted against the wrong variant/price.
    // VariantSelects.updateVariantInput() (assets/global.js) sets
    // input[name="id"].value and fires a bubbling `change` event on it —
    // that's the theme's real signal, so we listen for it here.
    if (this.productForm) {
      var idInput = this.productForm.querySelector('input[name="id"]');
      if (idInput) {
        idInput.addEventListener('change', function (event) {
          self.onVariantIdChange(event.target.value);
        });
        // Sync once on load in case the shopper arrived with a variant param.
        self.onVariantIdChange(idInput.value);
      }
    }
  };

  MakeOfferModal.prototype.onVariantIdChange = function (variantId) {
    var variant = this.variants.filter(function (v) {
      return String(v.id) === String(variantId);
    })[0];
    if (!variant) return;

    if (this.variantIdField) this.variantIdField.value = variant.id;
    this.currentVariantPrice = variant.price;

    if (this.priceEl) {
      this.priceEl.textContent = formatMoney(variant.price, this.root.getAttribute('data-shop-money-format'));
    }

    if (this.variantTitleEl) {
      var title = variant.title === 'Default Title' ? '' : variant.title;
      this.variantTitleEl.textContent = title;
      this.variantTitleEl.hidden = title === '';
    }

    if (this.imageEl && variant.featured_image && variant.featured_image.src) {
      this.imageEl.src = variant.featured_image.src;
      this.imageEl.removeAttribute('srcset');
    }
  };

  /* ---------------------------------------------------------------- open/close */

  MakeOfferModal.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocused = document.activeElement;

    this.root.hidden = false;
    lockScroll();

    // Clear any leftover messages from a previous open, and bring the submit
    // button back if the last visit ended in a successful send.
    this.clearErrors();
    if (this.submitBtn) this.submitBtn.hidden = false;

    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onKeydown);

    // Always start the modal body at the top, whatever the page scroll was.
    var body = this.root.querySelector('.make-offer-modal__body');
    if (body) body.scrollTop = 0;

    var firstField = this.root.querySelector('[data-make-offer-field="offer_price"]');
    if (firstField) firstField.focus({ preventScroll: true });
  };

  MakeOfferModal.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.root.hidden = true;
    unlockScroll();

    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onKeydown);

    var returnTo = this.trigger || this.lastFocused;
    if (returnTo && typeof returnTo.focus === 'function') {
      returnTo.focus({ preventScroll: true });
    }
  };

  MakeOfferModal.prototype.onKeydown = function (event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab' || !this.dialog) return;

    // Keep focus inside the dialog while it is open.
    var nodes = Array.prototype.filter.call(this.dialog.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!nodes.length) return;

    var first = nodes[0];
    var last = nodes[nodes.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------------------------------------------------------------- validation */

  MakeOfferModal.prototype.showFieldError = function (field, message) {
    var el = this.root.querySelector('[data-make-offer-error="' + field + '"]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    var input = this.root.querySelector('[data-make-offer-field="' + field + '"]');
    if (input) input.setAttribute('aria-invalid', 'true');
  };

  MakeOfferModal.prototype.clearErrors = function () {
    this.root.querySelectorAll('[data-make-offer-error]').forEach(function (el) {
      el.hidden = true;
      el.textContent = '';
    });
    this.root.querySelectorAll('[data-make-offer-field]').forEach(function (el) {
      el.removeAttribute('aria-invalid');
    });
    if (this.formError) {
      this.formError.hidden = true;
      this.formError.textContent = '';
    }
    if (this.success) {
      this.success.hidden = true;
      this.success.textContent = '';
    }
  };

  MakeOfferModal.prototype.validate = function (data) {
    var valid = true;
    var offer = parseFloat(data.offer_price);

    if (!data.offer_price || isNaN(offer)) {
      this.showFieldError('offer_price', this.t('offer_required'));
      valid = false;
    } else if (offer <= 0) {
      this.showFieldError('offer_price', this.t('offer_positive'));
      valid = false;
    } else if (this.minimumOffer > 0 && offer < this.minimumOffer) {
      this.showFieldError('offer_price', this.t('offer_minimum', { amount: this.minimumOffer.toFixed(2) }));
      valid = false;
    }

    if (!data.customer_name || !data.customer_name.trim()) {
      this.showFieldError('customer_name', this.t('name_required'));
      valid = false;
    }

    var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.customer_email || !emailPattern.test(data.customer_email)) {
      this.showFieldError('customer_email', this.t('email_invalid'));
      valid = false;
    }

    if (!valid) {
      var firstError = this.root.querySelector('[data-make-offer-error]:not([hidden])');
      if (firstError && firstError.scrollIntoView) {
        firstError.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    return valid;
  };

  MakeOfferModal.prototype.setLoading = function (isLoading) {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = isLoading;
    this.submitBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
  };

  /* -------------------------------------------------------------------- submit */

  MakeOfferModal.prototype.collect = function () {
    var self = this;
    var read = function (field) {
      var el = self.root.querySelector('[data-make-offer-field="' + field + '"]');
      return el && typeof el.value === 'string' ? el.value.trim() : '';
    };

    return {
      offer_price: read('offer_price'),
      customer_name: read('customer_name'),
      customer_email: read('customer_email'),
      customer_phone: read('customer_phone'),
      variant_id: read('variant_id'),
      product_id: this.root.getAttribute('data-product-id'),
      product_handle: this.root.getAttribute('data-product-handle'),
      product_title: this.root.getAttribute('data-product-title'),
      product_url: this.root.getAttribute('data-product-url') || '',
      currency: this.root.getAttribute('data-currency'),
      listed_price:
        typeof this.currentVariantPrice === 'number'
          ? (this.currentVariantPrice / 100).toFixed(2)
          : ''
    };
  };

  /**
   * Builds the readable message Shopify puts in the notification email body.
   */
  MakeOfferModal.prototype.buildBody = function (data) {
    return [
      this.t('offer_subject'),
      '',
      'Product: ' + (data.product_title || ''),
      'Listed price: ' + (data.currency || '') + ' ' + data.listed_price,
      'Offer: ' + (data.currency || '') + ' ' + data.offer_price,
      '',
      'Name: ' + data.customer_name,
      'Email: ' + data.customer_email,
      'Phone: ' + (data.customer_phone || '-'),
      '',
      'Variant ID: ' + (data.variant_id || ''),
      'Product ID: ' + (data.product_id || ''),
      'Product URL: ' + (data.product_url || '')
    ].join('\n');
  };

  /**
   * Submit handler.
   *
   * "contact" mode (default): validate, fill contact[body], then return true
   * so the BROWSER performs a normal form POST. Shopify handles the rest —
   * it emails the offer and redirects back to this page with
   * ?contact_posted=true, where Liquid renders the success message and
   * reopenAfterPost() below pops the modal open again.
   *
   * Posting this via fetch does not work — Shopify rejects XHR submissions of
   * the contact form, which is why earlier offers failed with a network error.
   * Do not "optimise" this back into an AJAX call.
   *
   * "app_proxy" mode: preventDefault and send JSON to the backend instead.
   */
  MakeOfferModal.prototype.onSubmit = function (event) {
    this.clearErrors();

    var data = this.collect();

    if (!this.validate(data)) {
      event.preventDefault();
      return;
    }

    if (this.bodyField) {
      this.bodyField.value = this.buildBody(data);
    }

    // "both" — fire the WhatsApp handoff, then let the native contact form
    // submit so the offer also lands in the sales inbox. window.open is
    // allowed here because we are still inside the shopper's click gesture.
    if (this.mode === 'both') {
      this.sendToWhatsApp(data);
      writeFlag(this.root.id);
      this.setLoading(true);
      document.body.classList.remove('make-offer-modal-open', 'overflow-hidden');
      return;
    }

    if (this.mode === 'whatsapp' || this.mode === 'email') {
      event.preventDefault();
      var sent =
        this.mode === 'whatsapp' ? this.sendToWhatsApp(data) : this.sendToEmail(data);
      if (sent) {
        this.showSuccess(null);
      } else {
        this.showFailure('no_destination_configured');
      }
      return;
    }

    if (this.mode === 'app_proxy') {
      event.preventDefault();
      var self = this;
      this.setLoading(true);
      this.sendToProxy(data)
        .then(function (result) {
          self.setLoading(false);
          self.showSuccess(result);
        })
        .catch(function (err) {
          self.setLoading(false);
          self.showFailure(err && err.message ? err.message : 'unknown');
        });
      return;
    }

    // Native submit — show the spinner while the page navigates, and record
    // which modal was sent so we can reopen it after Shopify redirects back.
    writeFlag(this.root.id);
    this.setLoading(true);
    // Keep scroll unlocked so the next page render is not stuck.
    document.body.classList.remove('make-offer-modal-open', 'overflow-hidden');
  };

  /**
   * "whatsapp" mode — opens WhatsApp with the offer pre-written.
   * Zero server, zero Shopify email config. Cannot fail: if WhatsApp is not
   * installed the browser opens web.whatsapp.com instead.
   */
  MakeOfferModal.prototype.sendToWhatsApp = function (data) {
    var number = (this.root.getAttribute('data-make-offer-whatsapp') || '').replace(/[^0-9]/g, '');
    if (!number) return false;

    var url = 'https://wa.me/' + number + '?text=' + encodeURIComponent(this.buildBody(data));
    window.open(url, '_blank', 'noopener');
    return true;
  };

  /**
   * "email" mode — opens the shopper's mail client with the offer pre-written.
   * Also cannot fail server-side, but relies on the device having mail set up.
   */
  MakeOfferModal.prototype.sendToEmail = function (data) {
    var to = this.root.getAttribute('data-make-offer-email') || '';
    if (!to) return false;

    var url =
      'mailto:' + to +
      '?subject=' + encodeURIComponent(this.t('offer_subject') + ' - ' + (data.product_title || '')) +
      '&body=' + encodeURIComponent(this.buildBody(data));
    window.location.href = url;
    return true;
  };

  /** Optional path — the App Proxy backend, once it exists. */
  MakeOfferModal.prototype.sendToProxy = function (data) {
    return fetch(this.proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).then(function (response) {
      if (!response.ok) throw new Error('request_failed');
      return response.json();
    });
  };

  /**
   * After Shopify redirects back, Liquid has already rendered the success or
   * error message inside the modal. Reopen it so the shopper sees the outcome
   * instead of landing on a silent product page.
   */
  MakeOfferModal.prototype.reopenAfterPost = function () {
    if (!/[?&]contact_posted=true/.test(window.location.search)) return;

    // form.posted_successfully? is true for EVERY contact form on the page,
    // including the theme's own product enquiry form. The flag set just
    // before submitting tells us the offer form is the one that was sent.
    if (readFlag() !== this.root.id) return;
    clearFlag();

    var successEl = this.root.querySelector('[data-make-offer-success]:not([hidden])');
    var errorEl = this.root.querySelector('[data-make-offer-form-error]:not([hidden])');
    var target = successEl || errorEl;
    if (!target) return;

    // open() clears leftover messages, so preserve what Liquid rendered.
    var text = target.textContent;
    this.open();
    target.textContent = text;
    target.hidden = false;

    if (successEl && this.submitBtn) this.submitBtn.hidden = true;
    if (target.scrollIntoView) target.scrollIntoView({ block: 'nearest' });
  };

  MakeOfferModal.prototype.showSuccess = function (result) {
    if (!this.success) return;

    var message = this.t('success');
    if (result && result.offer_id) {
      message = message + ' (Reference: ' + result.offer_id + ')';
    }

    this.success.textContent = message;
    this.success.hidden = false;
    this.success.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    if (this.form) this.form.reset();
    if (this.submitBtn) this.submitBtn.hidden = true;
  };

  MakeOfferModal.prototype.showFailure = function (reason) {
    if (!this.formError) return;
    var text = this.t('submit_failed');
    if (reason) {
      // Diagnostic suffix — tells us WHICH path failed instead of hiding it
      // behind one generic message like the previous builds did.
      text = text + ' [' + BUILD + '/' + this.mode + '/' + reason + ']';
    }
    this.formError.textContent = text;
    this.formError.hidden = false;
    this.formError.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  function init() {
    document.querySelectorAll('[data-make-offer-modal]').forEach(function (root) {
      if (root.dataset.makeOfferReady === 'true') return;
      root.dataset.makeOfferReady = 'true';

      // Move the modal to <body>. Any ancestor with a transform, filter or
      // will-change (sliders, animated sections) would otherwise become the
      // containing block for position:fixed and the overlay would only cover
      // part of the screen / sit under the sticky header.
      if (root.parentElement !== document.body) {
        document.body.appendChild(root);
      }

      new MakeOfferModal(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after a theme-editor section re-render.
  document.addEventListener('shopify:section:load', init);
})();
